import {
  ABANDONED_CART,
  BRANCHES,
  BUSINESS_30D,
  CART_FUNNEL,
  CHANNEL_MIX,
  CUSTOMER_SEGMENTS,
  DAYPARTS,
  DEMO_TODAY,
  FULFILMENT_BUCKETS,
  MENU_CATEGORIES,
  MENU_ITEMS,
  ORDER_TYPE_MIX,
  RETENTION_COHORTS,
  buildDailySeries,
  buildHeatmap,
  deterministicDelta,
  type ChannelId,
  type DailyPoint,
  type HeatmapCell,
  type MenuCategoryId,
  type OrderTypeId,
} from "../data/restaurantDemoData";

export interface RestaurantFilters {
  /** ISO date (YYYY-MM-DD), inclusive. */
  startDate: string;
  /** ISO date (YYYY-MM-DD), inclusive. */
  endDate: string;
  branchId: string;
  orderType: OrderTypeId | "all";
  channel: ChannelId | "all";
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const DEFAULT_END_DATE = isoDate(DEMO_TODAY);
const DEFAULT_START_DATE = (() => {
  const date = new Date(DEMO_TODAY);
  date.setUTCDate(date.getUTCDate() - 29);
  return isoDate(date);
})();

export const DEFAULT_RESTAURANT_FILTERS: RestaurantFilters = {
  startDate: DEFAULT_START_DATE,
  endDate: DEFAULT_END_DATE,
  branchId: "all",
  orderType: "all",
  channel: "all",
};

export interface WeeklyPoint {
  date: string;
  revenue: number;
  orders: number;
  aov: number;
}

export interface BranchMetric {
  id: string;
  revenue: number;
  orders: number;
  aov: number;
  share: number;
}

export interface OrderTypeMetric {
  id: OrderTypeId;
  revenue: number;
  orders: number;
  aov: number;
  share: number;
}

export interface ChannelMetric {
  id: ChannelId;
  revenue: number;
  orders: number;
  aov: number;
  share: number;
}

export interface DaypartMetric {
  id: string;
  revenue: number;
  orders: number;
}

export interface MenuItemMetric {
  id: string;
  units: number;
  revenue: number;
  attachRate: number;
  price: number;
  category: MenuCategoryId;
}

export interface CategoryMetric {
  id: MenuCategoryId;
  revenue: number;
  units: number;
}

export interface SegmentMetric {
  id: string;
  customers: number;
  revenue: number;
  ordersPerCustomer: number;
}

export interface FulfilmentMetric {
  id: string;
  orders: number;
  share: number;
}

export interface RetentionCohortMetric {
  cohort: string;
  size: number;
  retention: number[];
}

export interface CartMetric {
  abandonRate: number;
  abandonedCarts: number;
  lostRevenue: number;
  recoveredOrders: number;
  recoveredRevenue: number;
  recoveryRate: number;
}

export interface RestaurantMetrics {
  days: number;
  revenue: number;
  orders: number;
  aov: number;
  customers: number;
  newCustomers: number;
  repeatRate: number;
  cancelledOrders: number;
  refundedAmount: number;
  discountAmount: number;
  itemsPerOrder: number;
  fulfilmentMinutes: number;
  activeBranches: number;
  revenueDelta: number;
  ordersDelta: number;
  aovDelta: number;
  customersDelta: number;
  newCustomersDelta: number;
  repeatRateDelta: number;
  cancelledOrdersDelta: number;
  refundedAmountDelta: number;
  discountAmountDelta: number;
  itemsPerOrderDelta: number;
  fulfilmentMinutesDelta: number;
  unitsSoldDelta: number;
  daily: DailyPoint[];
  weekly: WeeklyPoint[];
  branches: BranchMetric[];
  orderTypes: OrderTypeMetric[];
  channels: ChannelMetric[];
  dayparts: DaypartMetric[];
  heatmap: HeatmapCell[];
  menu: MenuItemMetric[];
  categories: CategoryMetric[];
  segments: SegmentMetric[];
  fulfilment: FulfilmentMetric[];
  retention: RetentionCohortMetric[];
  cart: CartMetric;
}

function normalizeShares(items: { share: number }[]): number[] {
  const total = items.reduce((sum, item) => sum + item.share, 0);
  return items.map(item => (total > 0 ? item.share / total : 0));
}

/** Delivery waits on a driver; drive-thru and pickup are quickest. */
const FULFILMENT_TYPE_INDEX: Record<string, number> = {
  dine_in: 1.04,
  delivery: 1.32,
  takeaway: 0.88,
  drive_thru: 0.74,
  pickup: 0.81,
};

/** Aggregator hand-offs add time; kiosk and POS orders go straight to the line. */
const FULFILMENT_CHANNEL_INDEX: Record<string, number> = {
  mobile_app: 0.97,
  aggregator: 1.24,
  pos: 0.92,
  website: 1.02,
  kiosk: 0.9,
  call_center: 1.08,
};

/**
 * Items per order by order type — a seated table works through several courses,
 * a drive-thru run is a sandwich and a drink.
 */
const ITEMS_TYPE_INDEX: Record<string, number> = {
  dine_in: 1.38,
  delivery: 1.16,
  takeaway: 0.82,
  drive_thru: 0.61,
  pickup: 0.74,
};

/** Group orders arrive through the call centre and aggregators; kiosks are solo. */
const ITEMS_CHANNEL_INDEX: Record<string, number> = {
  mobile_app: 0.96,
  aggregator: 1.12,
  pos: 1.24,
  website: 1.04,
  kiosk: 0.68,
  call_center: 1.32,
};

/** Own-channel customers come back more often than aggregator traffic. */
const REPEAT_CHANNEL_INDEX: Record<string, number> = {
  mobile_app: 1.28,
  aggregator: 0.72,
  pos: 1.06,
  website: 0.94,
  kiosk: 0.85,
  call_center: 1.02,
};

const REPEAT_TYPE_INDEX: Record<string, number> = {
  dine_in: 1.08,
  delivery: 0.96,
  takeaway: 1.02,
  drive_thru: 1.12,
  pickup: 0.98,
};

/** Segment mix shifts by channel — aggregators skew new, app skews loyal. */
const SEGMENT_CHANNEL_SKEW: Record<string, Partial<Record<string, number>>> = {
  mobile_app: { new: 0.82, returning: 1.05, loyal: 1.34, at_risk: 0.88 },
  aggregator: { new: 1.42, returning: 0.96, loyal: 0.58, at_risk: 1.12 },
  pos: { new: 0.94, returning: 1.08, loyal: 1.12, at_risk: 0.92 },
  website: { new: 1.14, returning: 1.0, loyal: 0.86, at_risk: 1.04 },
  kiosk: { new: 1.22, returning: 0.98, loyal: 0.74, at_risk: 1.06 },
  call_center: { new: 0.96, returning: 1.04, loyal: 1.08, at_risk: 1.0 },
};

export function computeRestaurantMetrics(filters: RestaurantFilters): RestaurantMetrics {
  const { startDate, endDate, branchId, orderType, channel } = filters;
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1);
  const dayScale = days / 30;

  const branch = branchId === "all" ? null : BRANCHES.find(b => b.id === branchId) ?? null;
  const branchShare = branch ? branch.share : 1;

  const typeMix = orderType === "all" ? null : ORDER_TYPE_MIX.find(t => t.id === orderType) ?? null;
  const typeShare = typeMix ? typeMix.share : 1;
  const typeAovIndex = typeMix ? typeMix.aovIndex : 1;

  const channelMix = channel === "all" ? null : CHANNEL_MIX.find(c => c.id === channel) ?? null;
  const channelShare = channelMix ? channelMix.share : 1;
  const channelAovIndex = channelMix ? channelMix.aovIndex : 1;

  const seedKey = `${startDate}|${endDate}|${branchId}|${orderType}|${channel}`;

  const branchAovIndex = branch ? 0.92 + branch.rating * 0.04 : 1;
  const periodAovIndex = 1 + deterministicDelta(`${seedKey}:periodAov`, 0, 0.06);
  const aovIndex = typeAovIndex * channelAovIndex * branchAovIndex * periodAovIndex;
  const scale = dayScale * branchShare * typeShare * channelShare;

  const orders = Math.max(1, Math.round(BUSINESS_30D.orders * scale));
  const revenue = Math.round(orders * BUSINESS_30D.aov * aovIndex);
  const aov = orders > 0 ? revenue / orders : 0;


  const fulfilmentMinutes =
    (branch ? branch.avgPrepMinutes : BUSINESS_30D.avgFulfilmentMinutes) *
    (typeMix ? FULFILMENT_TYPE_INDEX[typeMix.id] ?? 1 : 1) *
    (channelMix ? FULFILMENT_CHANNEL_INDEX[channelMix.id] ?? 1 : 1) *
    (1 + deterministicDelta(`${seedKey}:fulfilmentLevel`, 0, 0.05));

  // Floored at 1.6: even the smallest real basket is more than a single item.
  const itemsPerOrder = Math.max(
    1.6,
    BUSINESS_30D.avgItemsPerOrder *
      (typeMix ? ITEMS_TYPE_INDEX[typeMix.id] ?? 1 : 1) *
      (channelMix ? ITEMS_CHANNEL_INDEX[channelMix.id] ?? 1 : 1) *
      (branch ? 0.9 + branch.seats / 1000 : 1) *
      (1 + deterministicDelta(`${seedKey}:itemsLevel`, 0, 0.14)),
  );

  // Loyalty skews by channel (app/POS regulars) and by order type.
  const repeatRate = Math.min(
    0.92,
    BUSINESS_30D.repeatRate *
      (channelMix ? REPEAT_CHANNEL_INDEX[channelMix.id] ?? 1 : 1) *
      (typeMix ? REPEAT_TYPE_INDEX[typeMix.id] ?? 1 : 1) *
      (1 + deterministicDelta(`${seedKey}:repeatLevel`, 0, 0.06)),
  );
  const rawDaily = buildDailySeries(days, seedKey, end);
  const rawOrdersTotal = rawDaily.reduce((sum, d) => sum + d.orders, 0) || 1;
  const rawRevenueTotal = rawDaily.reduce((sum, d) => sum + d.revenue, 0) || 1;

  const daily: DailyPoint[] = rawDaily.map(point => {
    const dayOrders = Math.max(0, Math.round((point.orders / rawOrdersTotal) * orders));
    const dayRevenue = Math.round((point.revenue / rawRevenueTotal) * revenue);
    const cancelled = Math.round(dayOrders * 0.028);
    const customers = Math.round(dayOrders * 0.61);
    return {
      date: point.date,
      orders: dayOrders,
      revenue: dayRevenue,
      cancelled,
      customers,
      aov: dayOrders > 0 ? dayRevenue / dayOrders : 0,
    };
  });

  const weekly: WeeklyPoint[] = [];
  for (let i = 0; i < daily.length; i += 7) {
    const chunk = daily.slice(i, i + 7);
    if (!chunk.length) {continue;}
    const chunkRevenue = chunk.reduce((sum, d) => sum + d.revenue, 0);
    const chunkOrders = chunk.reduce((sum, d) => sum + d.orders, 0);
    weekly.push({
      date: chunk[0].date,
      revenue: chunkRevenue,
      orders: chunkOrders,
      aov: chunkOrders > 0 ? chunkRevenue / chunkOrders : 0,
    });
  }

  const visibleBranches = branch ? [branch] : BRANCHES;
  const branchSharesNormalized = normalizeShares(visibleBranches);
  const branches: BranchMetric[] = visibleBranches
    .map((b, index) => {
      const share = branchSharesNormalized[index];
      const branchRevenue = Math.round(revenue * share);
      const branchOrders = Math.max(0, Math.round(orders * share));
      return {
        id: b.id,
        revenue: branchRevenue,
        orders: branchOrders,
        aov: branchOrders > 0 ? branchRevenue / branchOrders : 0,
        share,
      };
    })
    .sort((a, b) => b.revenue - a.revenue);

  const visibleTypes = typeMix ? [typeMix] : ORDER_TYPE_MIX;
  const typeSharesNormalized = normalizeShares(visibleTypes);
  const orderTypes: OrderTypeMetric[] = visibleTypes.map((mix, index) => {
    const share = typeSharesNormalized[index];
    const typeOrders = Math.max(0, Math.round(orders * share));
    const typeRevenue = Math.round(typeOrders * aov * (mix.aovIndex / aovIndex || 1));
    return {
      id: mix.id,
      orders: typeOrders,
      revenue: typeRevenue,
      aov: typeOrders > 0 ? typeRevenue / typeOrders : 0,
      share,
    };
  });

  const visibleChannels = channelMix ? [channelMix] : CHANNEL_MIX;
  const channelSharesNormalized = normalizeShares(visibleChannels);
  const channels: ChannelMetric[] = visibleChannels
    .map((mix, index) => {
      const share = channelSharesNormalized[index];
      const channelOrders = Math.max(0, Math.round(orders * share));
      // Per-channel basket drift keyed on the period, so the channel bars
      // re-rank between date ranges instead of holding one fixed order.
      const channelAov =
        aov *
        (mix.aovIndex / (channelMix ? channelAovIndex : 1)) *
        (1 + deterministicDelta(`${seedKey}:channelAov:${mix.id}`, 0, 0.08));
      const channelRevenue = Math.round(channelOrders * channelAov);
      return {
        id: mix.id,
        orders: channelOrders,
        revenue: channelRevenue,
        aov: channelOrders > 0 ? channelRevenue / channelOrders : 0,
        share,
      };
    })
    .sort((a, b) => b.revenue - a.revenue);

  const dayparts: DaypartMetric[] = DAYPARTS.map(part => ({
    id: part.id,
    orders: Math.round(orders * part.share),
    revenue: Math.round(revenue * part.share),
  }));

  const heatmap = buildHeatmap(orders);

  const totalUnits = orders * itemsPerOrder;
  const menu: MenuItemMetric[] = MENU_ITEMS.map(item => ({
    id: item.id,
    units: Math.round(totalUnits * item.unitShare),
    revenue: Math.round(totalUnits * item.unitShare * item.price),
    attachRate: item.attachRate,
    price: item.price,
    category: item.category,
  })).sort((a, b) => b.revenue - a.revenue);

  const categories: CategoryMetric[] = MENU_CATEGORIES.map(categoryId => {
    const items = menu.filter(item => item.category === categoryId);
    return {
      id: categoryId,
      revenue: items.reduce((sum, item) => sum + item.revenue, 0),
      units: items.reduce((sum, item) => sum + item.units, 0),
    };
  }).sort((a, b) => b.revenue - a.revenue);


  const channelSkew = channelMix ? SEGMENT_CHANNEL_SKEW[channelMix.id] ?? {} : {};
  const skewedSegments = CUSTOMER_SEGMENTS.map(segment => ({
    ...segment,
    weight: segment.share * (channelSkew[segment.id] ?? 1),
  }));
  const skewTotal = skewedSegments.reduce((sum, segment) => sum + segment.weight, 0) || 1;
  // Per-period drift so two equal-length ranges do not land on an identical count.
  const totalCustomers = Math.max(
    1,
    Math.round(
      BUSINESS_30D.customers * scale * (1 + deterministicDelta(`${seedKey}:customerBase`, 0, 0.07)),
    ),
  );

  const segments: SegmentMetric[] = skewedSegments
    .map(segment => {
      const segmentCustomers = Math.round(totalCustomers * (segment.weight / skewTotal));
      return {
        id: segment.id,
        customers: segmentCustomers,
        revenue: Math.round(segmentCustomers * segment.ordersPerCustomer * aov * segment.aovIndex),
        ordersPerCustomer: segment.ordersPerCustomer,
      };
    })
    .sort((a, b) => b.customers - a.customers);

  // Rounding each segment can drift from `totalCustomers`; trust the parts.
  const customers = segments.reduce((sum, segment) => sum + segment.customers, 0);
  const newCustomers = segments.find(segment => segment.id === "new")?.customers ?? 0;

  const fulfilment: FulfilmentMetric[] = FULFILMENT_BUCKETS.map(bucket => ({
    id: bucket.id,
    orders: Math.round(orders * bucket.share),
    share: bucket.share,
  }));

  const retentionIndex = repeatRate / BUSINESS_30D.repeatRate;
  const retention: RetentionCohortMetric[] = RETENTION_COHORTS.map(cohort => ({
    cohort: cohort.cohort,
    size: Math.max(1, Math.round(cohort.size * scale)),
    retention: cohort.retention.map((value, index) => {
      // Month 0 is always 100% by definition; later months move with loyalty.
      if (index === 0) {
        return 1;
      }
      const drift = 1 + deterministicDelta(`${seedKey}:cohort:${cohort.cohort}:${index}`, 0, 0.08);
      return Math.min(0.99, Math.max(0.02, value * retentionIndex * drift));
    }),
  }));

  const cart: CartMetric = {
    abandonRate: ABANDONED_CART.abandonRate,
    abandonedCarts: Math.round(ABANDONED_CART.abandonedCarts * scale),
    lostRevenue: Math.round(ABANDONED_CART.lostRevenue * scale),
    recoveredOrders: Math.round(ABANDONED_CART.recoveredOrders * scale),
    recoveredRevenue: Math.round(ABANDONED_CART.recoveredRevenue * scale),
    recoveryRate: ABANDONED_CART.recoveryRate,
  };

  // `deterministicDelta` returns a fraction; KPI cards render percentages.
  const asPercent = (fraction: number) => Number((fraction * 100).toFixed(1));

  const revenueFraction = deterministicDelta(`${seedKey}:revenue`, 0.084, 0.075);
  const ordersFraction = deterministicDelta(`${seedKey}:orders`, 0.061, 0.06);
  const revenueDelta = asPercent(revenueFraction);
  const ordersDelta = asPercent(ordersFraction);
  const aovDelta = asPercent((1 + revenueFraction) / (1 + ordersFraction) - 1);
  const customersDelta = asPercent(deterministicDelta(`${seedKey}:customers`, 0.047, 0.05));
  const newCustomersDelta = asPercent(deterministicDelta(`${seedKey}:newCustomers`, 0.068, 0.07));
  const repeatRateDelta = asPercent(deterministicDelta(`${seedKey}:repeatRate`, 0.021, 0.03));
  const cancelledOrdersDelta = asPercent(deterministicDelta(`${seedKey}:cancelled`, -0.034, 0.055));
  const refundedAmountDelta = asPercent(deterministicDelta(`${seedKey}:refunded`, -0.018, 0.06));
  const discountAmountDelta = asPercent(deterministicDelta(`${seedKey}:discount`, 0.052, 0.06));
  const itemsPerOrderDelta = asPercent(deterministicDelta(`${seedKey}:itemsPerOrder`, 0.014, 0.025));
  const fulfilmentMinutesDelta = asPercent(deterministicDelta(`${seedKey}:fulfilment`, -0.026, 0.04));
  const unitsSoldDelta = asPercent(deterministicDelta(`${seedKey}:unitsSold`, 0.073, 0.065));

  return {
    days,
    revenue,
    orders,
    aov,
    customers,
    newCustomers,
    repeatRate,
    cancelledOrders: Math.round(orders * 0.028),
    refundedAmount: Math.round(BUSINESS_30D.refundedAmount * scale),
    discountAmount: Math.round(BUSINESS_30D.discountAmount * scale),
    itemsPerOrder,
    fulfilmentMinutes,
    activeBranches: visibleBranches.length,
    revenueDelta,
    ordersDelta,
    aovDelta,
    customersDelta,
    newCustomersDelta,
    repeatRateDelta,
    cancelledOrdersDelta,
    refundedAmountDelta,
    discountAmountDelta,
    itemsPerOrderDelta,
    fulfilmentMinutesDelta,
    unitsSoldDelta,
    daily,
    weekly,
    branches,
    orderTypes,
    channels,
    dayparts,
    heatmap,
    menu,
    categories,
    segments,
    fulfilment,
    retention,
    cart,
  };
}

const FUNNEL_STAGE_IDS = CART_FUNNEL.map(stage => stage.id);
export { FUNNEL_STAGE_IDS };
